<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import { can, useDateTime } from '@/composables';
import { useOrganizationDocumentStore } from '@/store';
import { PERMISSIONS, STATUS_DATA_EXPORT } from '@/enum';
const organizationDocumentsStore = useOrganizationDocumentStore();

const show = ref(false);

onMounted(() => init());

const init = async () => {
  await getDocuments();
  show.value = true;
};

const getDocuments = async () => {
  if (can([PERMISSIONS.ORGANIZATION_DOCUMENTS_INDEX])) await organizationDocumentsStore.getOrganizationDataExports();
  else doFailUnauthorized('view');
};

const newDataExportIsAvailable = computed(() => {
  const status = organizationDocumentsStore.dataExport.status;
  return status === STATUS_DATA_EXPORT.PROCESSED || status === STATUS_DATA_EXPORT.FAILED || status === null;
});

const deleteDataExportIsAvailable = computed(() => organizationDocumentsStore.dataExport.documents.length > 0);

const messageTable = computed(() => {
  const { status } = organizationDocumentsStore.dataExport;

  if (status === STATUS_DATA_EXPORT.SCRUBBED) {
    return 'Data has been deleted.';
  }

  if (status === STATUS_DATA_EXPORT.PROCESSING || status === STATUS_DATA_EXPORT.PENDING) {
    return 'Data is exporting';
  }

  return 'No documents';
});

const doToast = (summary: string, detail: string) => {
  organizationDocumentsStore.doToast(detail, summary);
};

const doFailUnauthorized = (action: string) => {
  organizationDocumentsStore.doToast(`User unauthorized to ${action} documents.`, 'Error');
};

const onExportDocument = async () => {
  if (can([PERMISSIONS.ORGANIZATION_DOCUMENTS_GENERATE_DATA_EXPORT])) {
    const message = await organizationDocumentsStore.generateDataExport();
    doToast('Exporting', message.message);
    getDocuments();
  } else {
    doFailUnauthorized('export');
  }
};

const onDeleteDocument: () => Promise<void> = async () => {
  if (can([PERMISSIONS.ORGANIZATION_DOCUMENTS_DESTROY])) {
    await organizationDocumentsStore.deleteDataExport();
    getDocuments();
    doToast('Removed', 'Document has been removed');
  } else {
    doFailUnauthorized('delete');
  }
};

const onDownloadDocument = async (id: number, name: string) => {
  if (can([PERMISSIONS.ORGANIZATION_DOCUMENTS_DOWNLOAD])) {
    await organizationDocumentsStore.downloadGeneralDocument(id, name);
    doToast('Downloaded', 'Document successfully downloaded');
  } else {
    doFailUnauthorized('download');
  }
};
</script>

<template>
  <Card>
    <template #header>
      <div class="ml-auto py-3">
        <AppButton
          type="primary"
          text="Delete Export Files"
          icon-left="delete"
          :disabled="!deleteDataExportIsAvailable"
          @click="onDeleteDocument"
        />
      </div>
      <div class="ml-2 py-3">
        <AppButton
          type="primary"
          text="New Data Export"
          icon-left="cloud-upload"
          :disabled="!newDataExportIsAvailable"
          @click="onExportDocument"
        />
      </div>
    </template>
    <template #content>
      <DataTable
        :value="organizationDocumentsStore.dataExport.documents"
        :scrollable="true"
        loading-icon="logo-spinner"
        :loading="organizationDocumentsStore.loading"
        responsive-layout="scroll"
        scroll-height="440px"
      >
        <template v-if="show">
          <Column field="name" header="Filename">
            <template #body="{ data }">
              <a class="cloud-download" @click="onDownloadDocument(data.id, data.name)" v-text="data.name" />
            </template>
          </Column>
          <Column field="created_at" header="Created">
            <template #body="{ data }">
              <span v-text="useDateTime().formatIntlDate(data.created_at)" />
            </template>
          </Column>
        </template>
        <template #empty>{{ messageTable }}</template>
      </DataTable>
    </template>
  </Card>
</template>

<style scoped lang="scss">
.p-card {
  min-width: calc(100% - 1.5rem);
  min-height: 200px;

  :deep(.button.cloud-download .button-inner .button-text .button-icon-left) {
    max-width: 18px;
  }
}
</style>
